import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, Sun, Moon, User, Key, LogOut, Settings } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { api } from '../lib/api';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // ignore
    }
    logout();
    navigate('/login');
  };

  return (
    <header
      className="h-16 flex items-center justify-between px-6 border-b"
      style={{
        backgroundColor: 'var(--background-card)',
        borderColor: 'var(--border-color)',
      }}
    >
      {/* Left side */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          style={{ color: 'var(--text-primary)' }}
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg transition-colors"
          style={{ color: 'var(--text-secondary)' }}
        >
          {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
        </button>

        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="relative p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <Bell size={20} />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
          </button>

          {notificationsOpen && (
            <div
              className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg border z-50 animate-slideIn"
              style={{
                backgroundColor: 'var(--background-card)',
                borderColor: 'var(--border-color)',
              }}
            >
              <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  通知
                </h3>
              </div>
              <div className="max-h-80 overflow-y-auto">
                <div className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  新しい通知はありません
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Profile menu */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium"
                style={{ backgroundColor: 'var(--accent-color)' }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            )}
            <span
              className="hidden sm:block text-sm font-medium"
              style={{ color: 'var(--text-primary)' }}
            >
              {user?.name}
            </span>
          </button>

          {profileOpen && (
            <div
              className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg border z-50 animate-slideIn"
              style={{
                backgroundColor: 'var(--background-card)',
                borderColor: 'var(--border-color)',
              }}
            >
              {/* User info */}
              <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {user?.name}
                </p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {user?.email}
                </p>
                <span
                  className="inline-block mt-2 px-2 py-0.5 text-xs rounded-full"
                  style={{
                    backgroundColor: 'var(--light-gray-secondary)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {user?.role === 'admin'
                    ? '管理者'
                    : user?.role === 'tax_advisor'
                    ? '税理士'
                    : user?.role === 'accountant'
                    ? '経理担当'
                    : 'ユーザー'}
                </span>
              </div>

              {/* Menu items */}
              <div className="py-2">
                <button
                  onClick={() => {
                    setProfileOpen(false);
                    navigate('/profile');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <User size={16} />
                  プロフィール
                </button>
                <button
                  onClick={() => {
                    setProfileOpen(false);
                    navigate('/change-password');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <Key size={16} />
                  パスワード変更
                </button>
                {user?.role === 'admin' && (
                  <button
                    onClick={() => {
                      setProfileOpen(false);
                      navigate('/admin/settings');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <Settings size={16} />
                    設定
                  </button>
                )}
              </div>

              <div className="border-t py-2" style={{ borderColor: 'var(--border-color)' }}>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <LogOut size={16} />
                  ログアウト
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

